import { formatPrice, isDateAfter } from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import React from 'react';

import { GroupClassImage } from '../classes/GroupClassImage';

import { BootcampCohortList } from './BootcampCohortList';
import { BootcampEnrollCohortItem } from './BootcampEnrollCohortItem';
import { BootcampGetNotifiedInput } from './BootcampGetNotifiedInput';
import { type BootcampEnrollCardBootcampFragment } from './__generated-gql-types__/BootcampEnrollCard.generated';

interface BootcampEnrollCardProps {
  bootcamp: BootcampEnrollCardBootcampFragment;
}

export const BootcampEnrollCard = ({ bootcamp }: BootcampEnrollCardProps) => {
  const currentOrFutureCohorts = bootcamp.cohorts.filter((cohort) => {
    return cohort.endAt && isDateAfter(new Date(cohort.endAt), new Date());
  });
  const hasCurrentOrFutureCohorts = currentOrFutureCohorts.length > 0;

  return (
    <div className="ml-auto flex h-fit max-w-sm flex-col items-start justify-start rounded-lg shadow-lg sm:sticky sm:top-6 md:border">
      <GroupClassImage
        containerClassName="w-full"
        className="rounded-t-md object-cover"
        name={bootcamp.title}
        imageLink={bootcamp.imageLink}
        sizes={{ dflt: '100%' }}
      />

      <div className="flex w-full flex-col gap-5 p-5">
        <div className="flex gap-1">
          <div className="text-2xl font-medium">
            {formatPrice(bootcamp.price)}
          </div>
          {bootcamp.originalPrice ? (
            <span className="text-2xl text-leland-gray-extra-light line-through">
              {formatPrice(bootcamp.originalPrice)}
            </span>
          ) : null}
        </div>
        <div>
          <div className="text-sm font-medium uppercase tracking-wider text-leland-gray-light">
            {hasCurrentOrFutureCohorts
              ? 'Upcoming Cohorts'
              : 'No available cohorts'}
          </div>
          <BootcampCohortList
            cohorts={currentOrFutureCohorts}
            removeGap
            renderCohort={(cohort) => (
              <BootcampEnrollCohortItem
                key={cohort.id}
                cohort={cohort}
                maximumAttendees={bootcamp.maximumAttendees}
              />
            )}
          />
        </div>
        <BootcampGetNotifiedInput
          className="text-leland-gray-light"
          bootcampUrn={bootcamp.id}
        />
        {bootcamp.scholarshipUrl ? (
          <div className="text-center">
            <Link
              id="bootcamp_page:apply_for_scholarship_link"
              href={bootcamp.scholarshipUrl}
              className="text-lg font-medium underline"
            >
              Apply for a Scholarship
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
};
